import useExternalIntegrations from 'App/components/hooks/integrations/useExternalIntegrations';
import { useEffect, useMemo, useRef, useState } from 'react';
import logError from 'App/babel/helpers/logError';
import type {
	DataSourceSettings,
	ExternalIntegrationField
} from 'App/components/hooks/integrations/externalIntegrationTypes';
import { camelCase } from 'lodash';
import { getResourceInstance } from 'App/components/hooks/integrations/ExternalIntegrationModal/helpers/getResourceInstance';
import { useSelector } from 'App/components/hooks';

const useDataSources = (widgetExternalId: string, dataSourceSettings?: DataSourceSettings) => {
	const { getDataSource, getFields, user, getFieldsWithValues } = useExternalIntegrations();
	const [dataSources, setDataSources] = useState<Record<string, any>>();
	const [fields, setFields] = useState<ExternalIntegrationField[]>([]);
	const { allIntegrations } = useSelector(state => state.ExternalIntegration);

	// Store getDataSource in a ref to avoid dependency issues
	const getDataSourceRef = useRef(getDataSource);
	getDataSourceRef.current = getDataSource;

	// Store previous data source settings to prevent unnecessary re-renders
	const prevDataSourceSettingsRef = useRef<string>('');
	const currentDataSourceSettingsRef = useRef<DataSourceSettings | undefined>(dataSourceSettings);

	const pendingFetch = useRef(false);
	const fetchCountRef = useRef(0);
	const MAX_FETCHES = 5; // Prevent infinite loops

	const setupFields = async () => {
		const fields = await getFields(undefined, widgetExternalId);
		setFields(fields);
	};

	useEffect(() => {
		if (widgetExternalId) {
			setupFields();
			// Reset fetch count when widget changes
			fetchCountRef.current = 0;
		}
	}, [widgetExternalId]);

	const settings = useMemo(() => {
		return fields.reduce((acc, field) => {
			acc[camelCase(field.title)] = field.storedValue;
			return acc;
		}, {} as Record<string, any>);
	}, [fields]);

	useEffect(() => {
		if (user.authenticated && dataSourceSettings && !pendingFetch.current && allIntegrations.length > 0) {
			// Prevent infinite loops
			if (fetchCountRef.current >= MAX_FETCHES) {
				return;
			}

			// Create a cache key to prevent duplicate fetches
			const cacheKey = JSON.stringify(dataSourceSettings);

			// Check if settings have actually changed
			if (prevDataSourceSettingsRef.current === cacheKey) {
				return; // Skip if we've already fetched this exact configuration
			}

			pendingFetch.current = true;
			fetchCountRef.current += 1;
			prevDataSourceSettingsRef.current = cacheKey;
			currentDataSourceSettingsRef.current = dataSourceSettings;

			const fetchDataSources = async () => {
				const dataSourceResults: Record<string, any> = {};

				const providerData = user?.integrations?.[widgetExternalId]?.providerData || {};
				dataSourceResults.providerData = providerData;
				const integration = allIntegrations.find(i => i.type === widgetExternalId);
				if (integration) {
					const fields = await getFieldsWithValues(integration, widgetExternalId);
					dataSourceResults.fields = fields;
				}

				for (const [name, config] of Object.entries(dataSourceSettings)) {
					try {
						if (config.dataSourceType === 'upsales') {
							if (config.upsalesEntity) {
								// Entity already provided. just return it
								dataSourceResults[name] = config.upsalesEntity;
								continue;
							}
							// Handle Upsales data sources
							if (!config.upsalesResource) {
								dataSourceResults[name] = {
									error: 'upsalesResource is required for Upsales data sources'
								};
								continue;
							}

							if (!config.upsalesId) {
								dataSourceResults[name] = { error: 'upsalesId is required for Upsales data sources' };
								continue;
							}

							const resourceInstance = getResourceInstance(config.upsalesResource);
							const response = await resourceInstance.get(config.upsalesId);
							dataSourceResults[name] = response.data;
						} else {
							const response = await getDataSource(widgetExternalId, { [name]: config });
							dataSourceResults[name] = response[name];
						}
					} catch (error) {
						logError(error, `Error fetching data for data source: ${name}`);
						dataSourceResults[name] = { error: (error as Error).message || 'Failed to fetch data' };
					}
				}

				setDataSources(dataSourceResults);
			};

			fetchDataSources()
				.catch(err => {
					logError(err, 'Error fetching data sources');
				})
				.finally(() => {
					pendingFetch.current = false;
				});
		}
	}, [widgetExternalId, dataSourceSettings, user?.authenticated, allIntegrations]);

	return { dataSources, settings };
};

export default useDataSources;
